import React, { useCallback, useContext, useEffect, useState } from 'react';
import style from './style';
import { FormProvider, useForm } from 'react-hook-form';
import EAFormStepper from '../../../../components/common/EAFormStepper';
import i18next from 'i18next';
import EAButton from '../../../../components/common/EAButton';
import Add from '../../../Context/AddContext/components/Add';
import { Context } from '../../../../states/Store';
import { useHistory } from 'react-router-dom';
import { useAlert } from '../../../../components/common/hooks/useAlert';
import {
  getContextInstances,
  getContextInstanceThirdParty,
  getContextModel,
  saveContextInstance,
  saveContextInstanceThirdparty,
} from '../../../../services/edge/ContexteService';
import { ERROR } from '../../../../services/common/Constants';
import ROUTES from '../../../../config/Routes';
import StepperMainInfo from '../../../../components/context/stepper/StepperMainInfo';
import ContextInfos from '../../../../components/context/stepper/ContextInfos';
import { useLocation } from 'react-router';
import { getThirdparty } from '../../../../services/edge/ThirdpartyService';
import ModelSelection from './ModelSelection';
import EADialog from '../../../../components/common/EADialog';
import EAIcon from '../../../../components/common/EAIcon';

const defaultTpValues = {
  tpInfos: null,
  thirdPartyId: null,
  contextInstanceId: null,
  parentId: -1,
  tiersAttributes: [],
  referents: [],
};

export const defaultContextValues = {
  contextModelName: '',
  description: '',
  reference: '',
  contextAttributes: [],
};

export const defaultContextModelValues = {
  name: '',
  description: '',
  contextAttributes: [],
};

const AddTpContext = () => {
  const { classes } = style();
  let stepSetter;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const thirdpartyId = searchParams.get('thirdpartyId');
  const [state, dispatch] = useContext(Context);
  const history = useHistory();
  const contextModels = state.contextModels.filter((c) => c.active);
  const contextInstances = state.contextInstances;
  const { onError, onSuccess } = useAlert();
  const [showSuccess, setShowSuccess] = useState(false);
  const [isTpPresent, setIsTpPresent] = useState(false);
  const [isCheckingPresent, setIsCheckingPresent] = useState(false);
  const [savedContextThirdparty, setSavedContextThirdparty] = useState();
  const formMethods = useForm({
    mode: 'onChange',
    delayError: 500,
    defaultValues: {
      context: defaultContextValues,
      modelNewContext: defaultContextModelValues,
      tp: defaultTpValues,
    },
  });
  const getStepSetter = (setter) => {
    stepSetter = setter;
  };
  const { getValues, setValue, watch } = formMethods;
  const tiersAttributes = watch('tp.tiersAttributes');
  const tpToAdd = watch('tp.tpInfos');
  const referents = watch('tp.referents');
  const modelNewContext = watch('modelNewContext');
  const context = watch('context');

  const fetchThirdpartyInfos = useCallback(async () => {
    try {
      const result = await getThirdparty(
        state.account.id,
        thirdpartyId,
        dispatch
      );
      const tpToAdd = result.content;
      setValue('tp.thirdPartyId', tpToAdd.thirdpartyId);
      setValue('tp.tpInfos', tpToAdd);
    } catch (error) {
      dispatch({
        type: 'ALERT',
        alert: { type: ERROR, msg: 'THIRDPARTY_NOT_FOUND' },
      });
    }
  }, [state.account.id, thirdpartyId, dispatch, setValue]);

  useEffect(() => {
    if (modelNewContext.name !== '') {
      dispatch({
        type: 'IS_ASSOCIATING_CONTEXT_THIRDPARTY',
        isAssociatingContextThirdParty: true,
      });
    }
  }, [modelNewContext, dispatch]);

  useEffect(() => {
    fetchThirdpartyInfos();
    return () => {
      dispatch({
        type: 'IS_ASSOCIATING_CONTEXT_THIRDPARTY',
        isAssociatingContextThirdParty: false,
      });
    };
  }, [dispatch, fetchThirdpartyInfos]);

  const fetchContextModels = useCallback(async () => {
    try {
      await getContextModel(state.account.id, dispatch);
      dispatch({
        type: 'IS_CREATING_CONTEXT_INSTANCE',
        isCreatingContextInstance: true,
      });
    } catch (e) {
      dispatch({
        type: 'ALERT',
        alert: { type: ERROR, msg: 'INTERNAL_ERROR' },
      });
    }
  }, [state.account.id, dispatch]);

  const fetchContextInstances = useCallback(async () => {
    await getContextInstances(state.account.id, true, dispatch);
  }, [state.account, dispatch]);

  useEffect(() => {
    return () => {
      dispatch({
        type: 'IS_CREATING_CONTEXT_INSTANCE',
        isCreatingContextInstance: false,
      });
    };
  }, [dispatch]);

  useEffect(() => {
    fetchContextModels();
    fetchContextInstances();
  }, [state.account.id, fetchContextModels, fetchContextInstances]);

  useEffect(() => {
    const fetchIsPresentAtRank = async () => {
      setIsCheckingPresent(true)
      try {
        const thirdparties = await getContextInstanceThirdParty(
          state.account.id,
          context.id,
          dispatch
        );

        const isPresent = thirdparties.find(
          (tp) => tp.compteFoId === Number(thirdpartyId) && tp.parentId === -1
        );
        setIsTpPresent(isPresent);
      } finally {
        setIsCheckingPresent(false)
      }
    };
    if (context.id && thirdpartyId) {
      fetchIsPresentAtRank();
    }
  }, [context, thirdpartyId, dispatch, state.account.id]);

  const close = () => {
    history.replace(
      `/${state.account.id}/${ROUTES.THIRDPARTIES}/${thirdpartyId}`
    );
  };

  const createContext = async () => {
    const contextValues = getValues('context');
    try {
      const savedInstanceId = await saveContextInstance(
        state.account.id,
        contextValues
      );
      setValue('context.id', savedInstanceId);
      saveTiers(savedInstanceId);
    } catch (e) {
      dispatch({
        type: 'ALERT',
        alert: { type: ERROR, msg: 'INTERNAL_ERROR' },
      });
    }
  };

  const saveTiers = useCallback(async (contextInstanceId) => {
    const tpInfos = getValues('tp');
    const payload = [tpInfos].map(({ tpInfos, ...payloadInfos }) => ({
      ...payloadInfos,
      contextInstanceId,
    }));
    const savedContextThirdparty = await saveContextInstanceThirdparty(
      state.account.id,
      payload,
      dispatch
    );
    setSavedContextThirdparty(savedContextThirdparty);
    setShowSuccess(true);
    onSuccess(i18next.t('notifications.notifications.associated'));
  }, [dispatch, getValues, onSuccess, state.account.id]);

  const onEditAttribute = (data, attributeValue) => {
    const tiersAttributes = getValues('tp.tiersAttributes');
    setValue('tp.tiersAttributes', [
      ...tiersAttributes.map((att) => {
        if (att.attributeId === attributeValue.attributeId) {
          return { ...att, ...data };
        }
        return att;
      }),
    ]);
  };

  const addContact = (email) => {
    const referents = getValues('tp.referents');
    setValue('tp.referents', [...new Set([...referents, email])]);
  };

  const deleteReferent = (email) => {
    const referents = getValues('tp.referents');
    setValue(
      'tp.referents',
      referents.filter((r) => r !== email)
    );
  };

  const tpToExistingContext = async () => {
    const tpInfos = getValues('tp');
    const payload = [
      {
        ...tpInfos,
        contextInstanceId: getValues('context').id,
      },
    ].map(({ tpInfos, ...payloadInfos }) => payloadInfos);

    try {
      const savedContextThirdparty = await saveContextInstanceThirdparty(
        state.account.id,
        payload,
        dispatch
      );
      onSuccess(i18next.t('notifications.notifications.associated'));
      setSavedContextThirdparty(savedContextThirdparty);
      setShowSuccess(true);
    } catch (e) {
      onError('INTERNAL_ERROR');
    }
  };

  const onSubmit = async () => {
    const isNewContext = getValues('modelNewContext');
    if (isNewContext && isNewContext.id) {
      await createContext();
    } else {
      await tpToExistingContext();
    }
  };

  const Infos = ({
    title,
    subtitle,
    text,
    displayModifyButton = true,
    singleTp = null,
  }) => {
    return (
      <>
        <StepperMainInfo
          title={title}
          subtitle={subtitle}
          text={text}
          singleTp={tpToAdd}
        />
        <ContextInfos
          category={i18next.t('context.addTiers.context')}
          title={getValues('context').contextModelName}
          subtitle={getValues('context').reference}
          onEdit={() => stepSetter(0)}
          displayModifyButton={displayModifyButton}
        />
      </>
    );
  };

  const hideNextButton = !!isTpPresent || (!modelNewContext.id && !context.id) || isCheckingPresent;

  const stepsContent = [
    {
      id: 'param',
      name: i18next.t('thirdpartySheet.contextTp.breadCrumb1'),
      left: (
        <Infos
          title={i18next.t('thirdpartySheet.contextTp.addTpContext')}
          subtitle={null}
          text={i18next.t('thirdpartySheet.contextTp.addTpContextDescription')}
          displayModifyButton={false}
          singleTp={tpToAdd}
        />
      ),
      right: (
        <ModelSelection
          contextInstances={contextInstances}
          contextModels={contextModels}
          isTpPresent={isTpPresent}
          isCheckingPresent={isCheckingPresent}
        />
      ),
      hideNextButton: hideNextButton
    },
    {
      id: 'add',
      name: i18next.t('thirdpartySheet.contextTp.addStep'),
      left: (
        <Infos
          title={i18next.t('context.addTiers.add')}
          subtitle={null}
          text={i18next.t('thirdpartySheet.contextTp.addTpContextDescription')}
        />
      ),
      right: (
        <Add
          tiersAttributes={tiersAttributes}
          tpToAdd={tpToAdd}
          onEditAttribute={onEditAttribute}
          addContact={addContact}
          deleteReferent={deleteReferent}
          referents={referents}
        />
      ),
      hideInBreadCrumb: hideNextButton,

    },
  ];

  const successTitle =
    modelNewContext && modelNewContext.id
      ? i18next.t('thirdpartySheet.contextTp.successTitle')
      : i18next.t('thirdpartySheet.contextTp.successTitle');

  return (
    <div className={classes.root}>
      <EADialog
        isOpen={showSuccess}
        onClose={close}
        displayBtn={false}
        crossVisible={true}
        maxWidth={'lg'}
      >
        <div className={classes.successContentWrapper}>
          <div className={classes.successIconWrapper}>
            <EAIcon
              icon={'context/business_center'}
              className={classes.successIcon}
            />
          </div>
          <div className={classes.successTitle}>{successTitle}</div>
          <div className={classes.successContextName}>
            {getValues('context.reference')}
          </div>
          <div className={classes.successButtonWrapper}>
            <EAButton
              content={i18next.t('thirdpartySheet.contextTp.seeContextGeneral')}
              colorVariant={'primaryLight'}
              onClick={() =>
                history.replace(
                  `/${state.account.id}/${ROUTES.CONTEXT}?instance=${
                    getValues('context').id
                  }`
                )
              }
            />
            <EAButton
              content={i18next.t('thirdpartySheet.contextTp.seeContextTiers')}
              onClick={() =>
                history.replace(
                  `/${state.account.id}/${ROUTES.CONTEXT}?instance=${
                    getValues('context').id
                  }&thirdparty=${savedContextThirdparty[0]}`
                )
              }
            />
          </div>
        </div>
      </EADialog>
      <FormProvider {...formMethods}>
        <EAFormStepper
          title={i18next.t(`context.addContextInstance`)}
          stepsContent={stepsContent}
          onClose={() => {
            close();
          }}
          onNext={() => {}}
          checkValidStep={() => true}
          isFinalStep={true}
          finalStepText={i18next.t('thirdpartySheet.contextTp.saveText')}
          getStepSetter={getStepSetter}
          onSubmit={onSubmit}
        />
      </FormProvider>
    </div>
  );
};

export default AddTpContext;
